import React, { FC, useCallback } from "react";
import { LoadingSpinner } from "@xapp/components";
import {
    useGetMyNotificationPreferencesQuery,
    useGetSubscribableAppsQuery,
    useCreateAlertSubscriptionMutation,
    useUpdateAlertSubscriptionMutation,
    useDeleteAlertSubscriptionMutation,
    useCreateSummarySubscriptionMutation,
    useUpdateSummarySubscriptionMutation,
    useDeleteSummarySubscriptionMutation,
    useUpdateNotificationPhoneMutation,
    AlertSubscription,
    SummarySubscription,
} from "generated/graphql";
import { useWrapUrql } from "utils/graphql/useWrapUrql";
import { NotificationPreferencesView } from "../components/NotificationPreferencesView";

export const NotificationPreferencesContainer: FC = () => {
    const [preferencesData, reloadPreferences] = useGetMyNotificationPreferencesQuery();
    const [appsData] = useGetSubscribableAppsQuery();

    const createAlertSub = useWrapUrql(useCreateAlertSubscriptionMutation(), {
        onSuccessMessage: "Alert subscription created",
    });

    const updateAlertSub = useWrapUrql(useUpdateAlertSubscriptionMutation(), {
        onSuccessMessage: "Alert subscription updated",
    });

    const deleteAlertSub = useWrapUrql(useDeleteAlertSubscriptionMutation(), {
        onSuccessMessage: "Alert subscription deleted",
    });

    const createSummarySub = useWrapUrql(useCreateSummarySubscriptionMutation(), {
        onSuccessMessage: "Summary subscription created",
    });

    const updateSummarySub = useWrapUrql(useUpdateSummarySubscriptionMutation(), {
        onSuccessMessage: "Summary subscription updated",
    });

    const deleteSummarySub = useWrapUrql(useDeleteSummarySubscriptionMutation(), {
        onSuccessMessage: "Summary subscription deleted",
    });

    const updatePhone = useWrapUrql(useUpdateNotificationPhoneMutation(), {
        onSuccessMessage: "Phone number updated",
    });

    const handlePhoneUpdate = useCallback(
        (phone: string) => {
            updatePhone({ phone: phone || null })
                .then(() => {
                    reloadPreferences({ requestPolicy: "network-only" });
                })
                .catch((error) => {
                    // Error already handled by useWrapUrql
                    console.error("Failed to update phone number:", error);
                });
        },
        [updatePhone, reloadPreferences]
    );

    const handleCreateAlertSubscription = useCallback(
        (subscription: Partial<AlertSubscription>) => {
            if (!subscription.alertType || !subscription.appIds) {
                console.error("Alert type and app IDs are required");
                return;
            }

            createAlertSub({
                input: {
                    alertType: subscription.alertType,
                    appIds: subscription.appIds,
                    channel: subscription.channel,
                    filters: subscription.filters
                        ? {
                              contactType: subscription.filters.contactType,
                              highValueLead: subscription.filters.highValueLead,
                              jobType: subscription.filters.jobType,
                              urgency: subscription.filters.urgency,
                              serviceType: subscription.filters.serviceType,
                          }
                        : undefined,
                },
            })
                .then(() => {
                    reloadPreferences({ requestPolicy: "network-only" });
                })
                .catch((error) => {
                    // Error already handled by useWrapUrql
                    console.error("Failed to create alert subscription:", error);
                });
        },
        [createAlertSub, reloadPreferences]
    );

    const handleUpdateAlertSubscription = useCallback(
        (subscriptionId: string, subscription: Partial<AlertSubscription>) => {
            updateAlertSub({
                subscriptionId,
                input: {
                    appIds: subscription.appIds,
                    enabled: subscription.enabled,
                    filters: subscription.filters
                        ? {
                              contactType: subscription.filters.contactType,
                              highValueLead: subscription.filters.highValueLead,
                              jobType: subscription.filters.jobType,
                              urgency: subscription.filters.urgency,
                              serviceType: subscription.filters.serviceType,
                          }
                        : undefined,
                },
            })
                .then(() => {
                    reloadPreferences({ requestPolicy: "network-only" });
                })
                .catch((error) => {
                    // Error already handled by useWrapUrql
                    console.error("Failed to update alert subscription:", error);
                });
        },
        [updateAlertSub, reloadPreferences]
    );

    const handleDeleteAlertSubscription = useCallback(
        (subscriptionId: string) => {
            deleteAlertSub({ subscriptionId })
                .then(() => {
                    reloadPreferences({ requestPolicy: "network-only" });
                })
                .catch((error) => {
                    // Error already handled by useWrapUrql
                    console.error("Failed to delete alert subscription:", error);
                });
        },
        [deleteAlertSub, reloadPreferences]
    );

    const handleCreateSummarySubscription = useCallback(
        (subscription: Partial<SummarySubscription>) => {
            if (!subscription.appIds || !subscription.frequency) {
                console.error("App IDs and frequency are required");
                return;
            }

            createSummarySub({
                input: {
                    appIds: subscription.appIds,
                    frequency: subscription.frequency,
                    grouping: subscription.grouping,
                    channel: subscription.channel,
                },
            })
                .then(() => {
                    reloadPreferences({ requestPolicy: "network-only" });
                })
                .catch((error) => {
                    // Error already handled by useWrapUrql
                    console.error("Failed to create summary subscription:", error);
                });
        },
        [createSummarySub, reloadPreferences]
    );

    const handleUpdateSummarySubscription = useCallback(
        (subscriptionId: string, subscription: Partial<SummarySubscription>) => {
            updateSummarySub({
                subscriptionId,
                input: {
                    appIds: subscription.appIds,
                    frequency: subscription.frequency,
                    grouping: subscription.grouping,
                    enabled: subscription.enabled,
                },
            })
                .then(() => {
                    reloadPreferences({ requestPolicy: "network-only" });
                })
                .catch((error) => {
                    // Error already handled by useWrapUrql
                    console.error("Failed to update summary subscription:", error);
                });
        },
        [updateSummarySub, reloadPreferences]
    );

    const handleDeleteSummarySubscription = useCallback(
        (subscriptionId: string) => {
            deleteSummarySub({ subscriptionId })
                .then(() => {
                    reloadPreferences({ requestPolicy: "network-only" });
                })
                .catch((error) => {
                    // Error already handled by useWrapUrql
                    console.error("Failed to delete summary subscription:", error);
                });
        },
        [deleteSummarySub, reloadPreferences]
    );

    if (preferencesData.fetching || appsData.fetching) {
        return <LoadingSpinner loadingText="Loading notification preferences..." />;
    }

    return (
        <NotificationPreferencesView
            preferences={preferencesData.data?.myNotificationPreferences}
            error={preferencesData.error}
            subscribableApps={appsData.data?.subscribableApps || []}
            loading={preferencesData.fetching}
            onPhoneUpdate={handlePhoneUpdate}
            onCreateAlertSubscription={handleCreateAlertSubscription}
            onUpdateAlertSubscription={handleUpdateAlertSubscription}
            onDeleteAlertSubscription={handleDeleteAlertSubscription}
            onCreateSummarySubscription={handleCreateSummarySubscription}
            onUpdateSummarySubscription={handleUpdateSummarySubscription}
            onDeleteSummarySubscription={handleDeleteSummarySubscription}
        />
    );
};
